import React, {Component} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import muiThemeable from 'material-ui/styles/muiThemeable';
import {emphasize} from 'material-ui/utils/colorManipulator';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import DatePicker from 'material-ui/DatePicker';
import { defaultUtils } from 'material-ui/DatePicker/dateUtils';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import Chip from 'material-ui/Chip';
import DateIcon from 'material-ui/svg-icons/action/update';
import {grey800, grey300} from 'material-ui/styles/colors';
import Avatar from 'material-ui/Avatar';
import Snackbar from 'material-ui/Snackbar';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
// App
import './StepDate.css';
import API from './api';
import DateTimeFormat from "./utils/DateTimeFormat";

const styles = {
  chip: {
    margin: 'auto',
    marginBottom: '15px',
    transition: '0.2s ease-out',
    width: '-moz-fit-content',
  },
  wrapper: {
    marginTop: '40px',
    width: '100%',
    textAlign: 'center',
  },
};

class StepDate extends Component {

  static propTypes = {
    handleNext: PropTypes.func.isRequired,
    handlePrev: PropTypes.func.isRequired,
    formData: PropTypes.object.isRequired,
    onSetFormData: PropTypes.func.isRequired,
    onUpdateAvailableDays: PropTypes.func,
    getTotalServicesTime: PropTypes.func,
    getCheckedServicesId: PropTypes.func,
    allowEmployee: PropTypes.bool,
    allowFirstAvailable: PropTypes.bool,
    employees: PropTypes.shape({
      id: PropTypes.number,
      prenom: PropTypes.string,
      nom: PropTypes.nom,
    }),
    availableEmployees: PropTypes.arrayOf(PropTypes.number),
  };

  static defaultProps = {
    onUpdateAvailableDays: () => {},
    getTotalServicesTime: () => {},
    getCheckedServicesId: () => {},
    allowEmployee: true,
    allowFirstAvailable: false,
    employees: {},
  };

  state = {
    // Whether or not available time (after day selection) is loaded
    isAvailableTimeLoaded: false,
    // Whether or not snackbar info bar is open
    openSnackbar: false,
    isLoading: true,
  };

  /**
   * Key available days loading state in memory.
   * Avoid loading available days more than once.
   * @type {{}}
   */
  availableDaysLoaded = {};

  /**
   * Default DatePicker Utils functions.
   * We override some of them for custom behavior
   * @see this.buildDefaultDatePickerUtils()
   */
  defaultDatePickerUtils = defaultUtils;

  /**
   * DatePicker React component ref instance
   */
  datePickerRef;

  constructor(props, context) {
    super(props, context);

    this.buildDefaultDatePickerUtils();
  }

  componentDidMount() {
    // Load available days for current selected month
    this.loadAvailableDays(new Date());

    // Reload available times if date already set
    if (this.props.formData.date) {
      this.getAvailableTimes();
    }

    // Automatically open Datepicker on load
    // this.datePickerRef.refs.input.input.click();
  }

  componentDidUpdate(prevProps) {
    // Fetch available times when selected employee or date change
    if (this.props.formData.date) {
      if (prevProps.formData.employeeId !== this.props.formData.employeeId
        || prevProps.formData.date !== this.props.formData.date
      ) {
        this.getAvailableTimes();
      }
    }

    // Open calendar when new employee selected
    if (!this.props.date && prevProps.formData.employeeId !== this.props.formData.employeeId
      && this.datePickerRef
    ) {
      this._switchLoader(true);
      this.datePickerRef.refs.input.input.click();
    }
  }

  /**
   * Override default datePicker utils from material-ui.
   * We add custom hook inside datePicker events.
   */
  buildDefaultDatePickerUtils() {
    /*
     AVAILABLE UTILS FUNCTIONS FOR DATEPICKER:
     const monthDiffBase = defaultUtils.monthDiff;
     const addMonthsBase = defaultUtils.addMonths;
     const setYearBase = defaultUtils.setYear;
     const addDaysBase = defaultUtils.addDays;
     const addYearsBase = defaultUtils.addYears;
     const getFirstDayOfMonthBase = defaultUtils.getFirstDayOfMonth;
     const getYearBase = defaultUtils.getYear;

     AVAILABLE HOOK EVENTS FOR DATEPICKER:
     defaultUtils.monthDiff = (...args) => { console.log('next month', ...args); return monthDiffBase(...args); };
     defaultUtils.addMonths = (...args) => { console.log('add month', ...args); return addMonthsBase(...args); };
     defaultUtils.setYear = (...args) => { console.log('setYear', ...args); return setYearBase(...args); };
     defaultUtils.addDays = (...args) => { console.log('addDays', ...args); return addDaysBase(...args); };
     defaultUtils.addMonths = (...args) => { console.log('addMonths', ...args); return addMonthsBase(...args); };
     defaultUtils.addYears = (...args) => { console.log('addYears', ...args); return addYearsBase(...args); };
     defaultUtils.getFirstDayOfMonth = (...args) => { console.log('getFirstDayOfMonth', ...args); return getFirstDayOfMonthBase(...args); };
     defaultUtils.getYear = (...args) => { console.log('getYear', ...args); return getYearBase(...args); };
     */

    // We override getWeekArray event from Datepicker materail-ui component.
    // This is currently the only way to have a event fired when
    // user switch to a new month (we use it to load available days for this month)
    const getWeekArrayBase = defaultUtils.getWeekArray;

    this.defaultDatePickerUtils.getWeekArray = (date, value) => {
      // Load available days for this month
      this.loadAvailableDays(date);

      // Load available days for next month (only for better UX)
      const dateNextMonth = moment(date).add(1, 'months');
      this.loadAvailableDays(dateNextMonth);

      // Call the original parent function
      return getWeekArrayBase(date, value);
    };
  }

  /**
   * Event fired when Date (day) change
   *
   * @param event
   * @param date
   */
  onChangeDate = (event, date) => {
    this.props.onSetFormData({
      date: date,
      time: null,
      availableTimes: []
    });
  };

  /**
   * Event fired when an available time is selected
   *
   * @param {string} time identifier (ex: '1330')
   */
  onSelectTime = (time) => {
    // If already checked, unselect it
    if (this.props.formData.time && this.props.formData.time.value === time.value) {
      this.props.onSetFormData({
        time: null
      });
    }
    else {
      this.props.onSetFormData({
        time: time,
      });
    }
  };

  /**
   * Whether or not a time is selected
   *
   * @param {String} time identifier (ex: '1600')
   * @returns {boolean}
   */
  isSelectedTime(time) {
    return (this.props.formData.time && this.props.formData.time.value === time);
  }

  getTotalServicesTime() {
    let time = 0;

    Object.keys(this.props.formData.checkedServices).forEach(serviceKey => {
      Object.keys(this.props.formData.checkedServices[serviceKey]).forEach(subServiceKey => {

        if (this.props.formData.checkedServices[serviceKey][subServiceKey]) {
          time += this.props.services[serviceKey].services[subServiceKey].time;
        }
      });
    });

    return time;
  }

  /**
   * Fetch available days for a specific month following day and duration.
   * Will perform an ajax request to the API server.
   *
   */
  loadAvailableDays(date) {
    date = moment(date).format('DD-MM-YYYY');

    // Fetch available days for the selected month (only if not loaded yet)
    // AND check that calendar instance is available
    if (!this.availableDaysLoaded[date]) {
      API.getAvailableDays(date, 60 * this.props.getTotalServicesTime(), JSON.stringify(this.props.getCheckedServicesId()), this.props.formData.employeeId)
        .then(response => {
          // Add new availableDays
          const newAvailableDays = Object.assign(this.props.formData.availableDays, response.data.data);
          this.props.onUpdateAvailableDays({
            availableDays: newAvailableDays
          });

          // Most ugliest thing ever done
          // Get current date in the Calendar (first day of selected month)
          // Only access datePickerRef if it exists (it's conditionally rendered)
          if (this.datePickerRef && this.datePickerRef.refs && this.datePickerRef.refs.dialogWindow) {
            const displayedDate = moment(this.datePickerRef.refs.dialogWindow.refs.calendar.state.displayDate);
            // Display a message if no day available
            this._openNoDateSnackbarIfNeeded(displayedDate, newAvailableDays);
          }

          this._switchLoader(false);
        })
        .catch(error => {
          console.log('ERROR when fetching available days. Please try again', error);

          this._switchLoader(false);

          this.availableDaysLoaded[date] = false;
        });
    }

    this.availableDaysLoaded[date] = true;
  }

  /**
   * Open "no available date" SnackBar if needed.
   * Check if all days for this month are available
   * @param {moment} date - Current displayed date
   * @param {Object} availableDays - Map of all available days
   * @private
   */
  _openNoDateSnackbarIfNeeded = (date, availableDays) => {
    if (!this._isMonthAvailable(date.month() + 1, availableDays)) {
      this.setState({
        openSnackbar: true,
      });
    }
  };

  _switchLoader = (flag = true) => {
    this.setState({
      isLoading: flag,
    });
  };

  /**
   * Check if a month is available
   * @param {Number{ monthNumber - 0 = January
   * @param {Object} availableDays - Object of available days
   * @returns {boolean}
   * @private
   */
  _isMonthAvailable(monthNumber, availableDays) {
    let isAvailable = false;
    const regexMatchMonth = new RegExp(`^[0-9]{2}-0${monthNumber}-[0-9]{4}$`);

    for (let date in availableDays) {
      // If day of this month is available, month is available
      if (!isAvailable && date.match(regexMatchMonth) && availableDays[date]) {
        isAvailable = true;
        break;
      }
    }

    return isAvailable;
  }

  /**
   * Event fired when "no date" Snackbar wants to close itself.
   */
  handleRequestCloseSnackbar = () => {
    this.setState({
        openSnackbar: false,
    });
  };

  /**
   * Fetch available time following day and duration.
   * Will perform an ajax request to API server.
   *
   */
  getAvailableTimes() {
    let date = moment(this.props.formData.date);

    this.setState({
      isAvailableTimeLoaded: false
    });

    // Fetch services and categories from server API
    API.getAvailableTimes(date.format('DD-MM-YYYY'), 60 * this.props.getTotalServicesTime(), JSON.stringify(this.props.getCheckedServicesId()), this.props.formData.employeeId)
      .then(response => {

        setTimeout(() => {
          this.props.onSetFormData({
            availableTimes: response.data.data
          });

          this.setState({
            isAvailableTimeLoaded: true
          });
        }, 200);
      });
  }


  /**
   * Return Chip (available time) style following container
   *
   * @param {String} time identifier (ex: '1100' for 11AM)
   * @param {String} style identifier (main, color, background, backgroundIcon)
   * @returns {*} style
   */
  getChipStyle(time, style = 'background') {
    if (this.isSelectedTime(time)) {
      switch (style) {
        case 'background':
          return this.props.muiTheme.palette.accent1Color;
        case 'backgroundIcon':
          return emphasize(this.props.muiTheme.palette.accent1Color);
        case 'color':
          return this.props.muiTheme.palette.alternateTextColor;
        case 'main':
          return _.merge({}, styles.chip, {padding: '0px 10px'});
        default:
          return {};
      }
    }
    else {
      switch (style) {
        case 'background':
          return grey300;
        case 'backgroundIcon':
          return emphasize(grey300);
        case 'color':
          return grey800;
        case 'main':
          return styles.chip;
        default:
          return {};
      }
    }

  }

  /**
   * Whether or not this day is busy (no free time anymore).
   * This is used by DatePicker to disable days for user.
   *
   * @param {Date} date
   * @returns {boolean}
   */
  isBusyDay = (date) => {
    const formattedDate = '' + moment(date).format('DD-MM-YYYY');

    // If information doesn't exist, we assume this day is available
    if (undefined === this.props.formData.availableDays[formattedDate]) {
      return false;
    }

    // If not, we return day availability
    return !this.props.formData.availableDays[formattedDate];
  };

  handleChangeEmployee = (event, index, value) => {
    // Reset loaded days in calendar
    this.availableDaysLoaded = {};

    this.props.onSetFormData({
      employeeId: value,
      // Reset date and available times
      date: null,
      time: null,
      availableTimes: []
    });
  };

  renderEmployees() {
    if (!this.props.allowEmployee) return null;

    const options = [];

    Object.keys(this.props.employees).forEach(employeeId => {
     const employee = this.props.employees[employeeId];
      options.push(
        <MenuItem
          key={employee.id}
          value={employee.id}
          primaryText={`${employee.prenom} ${employee.nom}`}
          disabled={this.props.availableEmployees.indexOf(employee.id) === -1}
        />
      );
    });

    return (
      <SelectField
        floatingLabelText="Collaborateur"
        value={this.props.formData.employeeId}
        onChange={this.handleChangeEmployee}
        hintText="Choisissez un membre du personnel"
        style={{ width: 230 }}
      >
        {this.props.allowFirstAvailable ? (
          <MenuItem value="0" primaryText="Premier disponible" />
        ) : (
          <MenuItem value="0" primaryText="Choisir un collaborateur" />
        )}
        {options}
      </SelectField>
    );
  }

  renderTimeChips() {
    let content = [];

    this.props.formData.availableTimes.forEach(availableTime => {
      content.push(
        <Chip
          key={availableTime.value}
          className="step-date-chip"
          style={this.getChipStyle(availableTime.value, 'main')}
          backgroundColor={this.getChipStyle(availableTime.value)}
          labelColor={this.getChipStyle(availableTime.value, 'color')}
          onClick={this.onSelectTime.bind(null, availableTime)}
        >
          <Avatar
            color={this.getChipStyle(availableTime.value, 'color')}
            icon={<DateIcon />}
            backgroundColor={this.getChipStyle(availableTime.value, 'backgroundIcon')}
          />
          {availableTime.label}
        </Chip>
      );
    });

    return content;
  }

  renderAvailableDatetime() {
    if (!this.props.formData.date) {
      return null;
    }

    const hasTimesAvailable = (this.props.formData.availableTimes && this.props.formData.availableTimes.length > 0);
    let Loader, PickAnotherDay;

    if (!hasTimesAvailable && this.state.isAvailableTimeLoaded) {
      Loader = (
        <div>
          Aucun horaire disponible pour cette date.<br />
          Choisissez une autre date.
        </div>
      );
    }
    else if (!hasTimesAvailable) {
      Loader = (
        <RefreshIndicator
          size={40}
          left={10}
          top={0}
          status="loading"
          className="step-date-loader"
        />
      );
    }
    else {
      PickAnotherDay = (
        <div
          onClick={() => this.props.onSetFormData({ date: null, time: null })}
          className="step-date-pick-day"
          style={{ color: this.props.muiTheme.palette.primary1Color }}
        >
          Choisir un autre jour
        </div>
      );
    }

    return (
      <div>
        <div
          className="step-date-content-container"
          style={{ marginTop: hasTimesAvailable ? '30px' : '60px' }}
        >
          {Loader}
          <div className="step-date-loader-label">
            {hasTimesAvailable ? 'Choisissez un créneau horaire' : 'CHARGEMENT DES HORAIRES'}
          </div>
        </div>
        <div className="step-date-times-container">
          <CSSTransitionGroup
            transitionName="example"
            transitionEnterTimeout={500}
            transitionLeaveTimeout={300}
          >
            {this.renderTimeChips()}
          </CSSTransitionGroup>
        </div>
        {PickAnotherDay}
      </div>
    );
  }

  renderLoader() {
    if (this.state.isLoading) {
      return (
        <div className="step-date-main-loader">
          <RefreshIndicator
            size={40}
            left={0}
            top={300}
            status="loading"
            className="step-date-loader"
          />
        </div>
      );
    }
  }

  render() {
    return (
      <div>
        {this.renderLoader()}
        <div
          className="step-date-container"
          style={{
            marginTop: this.props.formData.date ? -20 : 100,
          }}
        >
          {this.renderEmployees()}
          {(!this.props.allowEmployee || this.props.allowFirstAvailable ||
            this.props.formData.employeeId != 0
          ) && (
            <DatePicker
              ref={(instance) => (this.datePickerRef = instance)}
              value={this.props.formData.date}
              DateTimeFormat={DateTimeFormat}
              floatingLabelText={
                this.props.formData.date
                  ? "Choisir une autre date"
                  : "Cliquez ici pour choisir une date"
              }
              minDate={new Date()}
              onChange={this.onChangeDate}
              textFieldStyle={{
                maxWidth: this.props.formData.date ? "230px" : "230px",
              }}
              disableYearSelection
              shouldDisableDate={this.isBusyDay}
              mode={window.innerWidth > 500 ? "landscape" : "portrait"}
              locale={"fr-FR"}
              utils={this.defaultDatePickerUtils}
              autoOk
              inputStyle={{
                textAlign: "left",
              }}
              floatingLabelStyle={{
                width: "100%",
                left: null,
              }}
            />
          )}

          <Snackbar
            open={this.state.openSnackbar}
            message="Aucune date disponible pour ce mois."
            autoHideDuration={5000}
            onRequestClose={this.handleRequestCloseSnackbar}
          />
        </div>

        {this.renderAvailableDatetime()}

        <div className="step-date-footer">
          <FlatButton
            label="Précédent"
            onClick={this.props.handlePrev}
            className="step-date-btn-prev"
          />
          <RaisedButton
            label="Suivant"
            primary={true}
            disabled={
              !this.props.formData.time ||
              (this.props.allowEmployee && !this.props.allowFirstAvailable && this.props.formData.employeeId == 0)
            }
            onClick={this.props.handleNext}
          />
        </div>
      </div>
    );
  }
}

export default muiThemeable()(StepDate);


// WEBPACK FOOTER //
// ./src/StepDate.js